/**
 * Preview Building Builder
 *
 * Utility functions to create preview buildings from store data
 * using the template-based rendering system.
 */

import * as THREE from 'three'
import type { SingleUnitFormValues, PreviewBuilding, DoorsConfig, DoorWall, TemplateType } from '@/stores/buildingEditorStore'
import { createEmptyDoorsConfig } from '@/stores/buildingEditorStore'
import { getSingleUnitTemplate } from '@/lib/templates'
import { renderSingleUnitBuilding } from '../orchestrators/SingleUnitOrchestrator'
import { renderSingleRowBuilding } from '../orchestrators/SingleRowOrchestrator'
import { renderDoubleRowBuilding } from '../orchestrators/DoubleRowOrchestrator'
import { renderTRowBuilding } from '../orchestrators/TRowOrchestrator'
import { renderNestedTRowBuilding } from '../orchestrators/NestedTRowOrchestrator'
import { renderArchHangarBuilding } from '../orchestrators/ArchHangarOrchestrator'
import { generateBuilding, generateUnits } from '@/lib/templates/single-row/generate'
import {
  generateBuilding as generateDoubleRowBuilding,
  generateUnits as generateDoubleRowUnits,
} from '@/lib/templates/double-row/generate'
import {
  generateBuilding as generateTHangarBuilding,
  generateUnits as generateTHangarUnits,
} from '@/lib/templates/standard-t-hangar/generate'
import {
  generateBuilding as generateNestedTHangarBuilding,
  generateUnits as generateNestedTHangarUnits,
} from '@/lib/templates/nested-t-hangar/generate'
import {
  generateBuilding as generateArchHangarBuilding,
  generateUnits as generateArchHangarUnits,
} from '@/lib/templates/arch-hangar/generate'
import type { SingleRowParams } from '@/lib/templates/single-row/params'
import type { DoubleRowParams } from '@/lib/templates/double-row/params'
import type { THangarParams } from '@/lib/templates/standard-t-hangar/params'
import type { NestedTHangarParams } from '@/lib/templates/nested-t-hangar/params'
import type { ArchHangarParams } from '@/lib/templates/arch-hangar/params'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'

// ============================================================================
// TYPES
// ============================================================================

/** Union of all form value types */
type FormParams = SingleUnitFormValues | SingleRowParams | DoubleRowParams | THangarParams | NestedTHangarParams | ArchHangarParams

/** Template config for unified preview builder */
interface TemplateConfig<T extends FormParams> {
  generateBuilding: (params: T) => GeneratedBuilding
  generateUnits: (building: GeneratedBuilding, params: T) => GeneratedUnit[]
  render: (building: GeneratedBuilding, units: GeneratedUnit[], options: RenderOptions) => THREE.Group
  previewId: string
}

interface RenderOptions {
  buildingId: string
  buildingName: string
  position: [number, number, number]
  rotationDeg: number
  enableShadows: boolean
}

// ============================================================================
// TEMPLATE REGISTRY
// ============================================================================

/**
 * Get template configuration by type
 * Centralizes the mapping between template types and their generators/renderers
 */
function getTemplateConfig<T extends FormParams>(templateType: TemplateType): TemplateConfig<T> | null {
  const configs: Record<string, TemplateConfig<FormParams>> = {
    single_unit_community: {
      generateBuilding: (params) => {
        const template = getSingleUnitTemplate()
        return template.generateBuilding({
          ...params,
          latitude: 0,
          longitude: 0,
        } as SingleUnitFormValues)
      },
      generateUnits: (building, params) => {
        const template = getSingleUnitTemplate()
        return template.generateUnits(building, {
          ...params,
          latitude: 0,
          longitude: 0,
        } as SingleUnitFormValues)
      },
      render: renderSingleUnitBuilding,
      previewId: 'preview-building',
    },
    single_row: {
      generateBuilding: (params) => generateBuilding(params as SingleRowParams),
      generateUnits: (building, params) => generateUnits(building, params as SingleRowParams),
      render: renderSingleRowBuilding,
      previewId: 'preview-single-row',
    },
    double_row: {
      generateBuilding: (params) => generateDoubleRowBuilding(params as DoubleRowParams),
      generateUnits: (building, params) => generateDoubleRowUnits(building, params as DoubleRowParams),
      render: renderDoubleRowBuilding,
      previewId: 'preview-double-row',
    },
    standard_t_hangar: {
      generateBuilding: (params) => generateTHangarBuilding(params as THangarParams),
      generateUnits: (building, params) => generateTHangarUnits(building, params as THangarParams),
      render: renderTRowBuilding,
      previewId: 'preview-t-hangar',
    },
    nested_t_hangar: {
      generateBuilding: (params) => generateNestedTHangarBuilding(params as NestedTHangarParams),
      generateUnits: (building, params) => generateNestedTHangarUnits(building, params as NestedTHangarParams),
      render: renderNestedTRowBuilding,
      previewId: 'preview-nested-t-hangar',
    },
    arch_hangar: {
      generateBuilding: (params) => generateArchHangarBuilding(params as ArchHangarParams),
      generateUnits: (building, params) => generateArchHangarUnits(building, params as ArchHangarParams),
      render: renderArchHangarBuilding,
      previewId: 'preview-arch-hangar',
    },
  }

  return (configs[templateType] as TemplateConfig<T>) || null
}

// ============================================================================
// UNIFIED PREVIEW BUILDER
// ============================================================================

/**
 * Create a preview building using the unified template registry
 *
 * @param templateType - The template type to use
 * @param params - Form parameters for the building
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry, or null if template not found
 */
export function createPreviewBuilding<T extends FormParams>(
  templateType: TemplateType,
  params: T,
  worldPosition: [number, number, number]
): THREE.Group | null {
  const config = getTemplateConfig<T>(templateType)
  if (!config) {
    console.warn(`createPreviewBuilding: Unknown template type "${templateType}"`)
    return null
  }

  // Generate building and units using template-specific functions
  const buildingConfig = config.generateBuilding(params)
  const unitConfigs = config.generateUnits(buildingConfig, params)

  // Get name from params (all form types have a 'name' field)
  const name = 'name' in params ? (params as { name: string }).name : 'Preview'

  // Get bearing from params (all form types have 'bearing_deg' field)
  const bearingDeg = 'bearing_deg' in params ? (params as { bearing_deg: number }).bearing_deg : 0

  // Render using the template-specific orchestrator
  return config.render(buildingConfig, unitConfigs, {
    buildingId: config.previewId,
    buildingName: name,
    position: worldPosition,
    rotationDeg: bearingDeg,
    enableShadows: true,
  })
}

// ============================================================================
// BACKWARD COMPATIBLE FUNCTIONS
// These wrap the unified createPreviewBuilding for existing code
// ============================================================================

/**
 * Create a preview building group from form values and position
 * @deprecated Use createPreviewBuilding('single_unit_community', formValues, worldPosition) instead
 *
 * @param formValues - Form values for the building
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createPreviewFromFormValues(
  formValues: SingleUnitFormValues,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('single_unit_community', formValues, worldPosition)
  // Fallback to empty group if template fails (should never happen)
  return result || new THREE.Group()
}

/**
 * Create a preview building group from PreviewBuilding store data
 *
 * @param previewBuilding - Preview building from the store
 * @returns Three.js Group containing the building geometry
 */
export function createPreviewFromStoreData(
  previewBuilding: PreviewBuilding
): THREE.Group {
  // Convert flat doors array to DoorsConfig grouped by wall
  const doors: DoorsConfig = createEmptyDoorsConfig()
  for (const door of previewBuilding.doors) {
    doors[door.wall as DoorWall].push({
      offset_ft: door.offset_ft,
      width_ft: door.width_ft,
      height_ft: door.height_ft,
      type: door.type,
    })
  }

  // Convert PreviewBuilding to form values format
  const formValues: SingleUnitFormValues = {
    name: previewBuilding.name,
    code: previewBuilding.code,
    width_ft: previewBuilding.width_ft,
    depth_ft: previewBuilding.depth_ft,
    height_ft: previewBuilding.height_ft,
    elevation_ft: 0, // Always use 0 elevation
    doors,  // Multi-door support
    floor_color: previewBuilding.floor_color,
    wall_color: previewBuilding.wall_color,
    bearing_deg: previewBuilding.bearing_deg,
    latitude: previewBuilding.latitude,
    longitude: previewBuilding.longitude,
  }

  // Use world coordinates from store (elevation hardcoded to 0)
  const worldPosition: [number, number, number] = [
    previewBuilding.worldX,
    0, // Always use 0 elevation
    previewBuilding.worldZ,
  ]

  return createPreviewFromFormValues(formValues, worldPosition)
}

/**
 * Apply preview building styling (semi-transparent for visual distinction)
 */
export function applyPreviewStyling(group: THREE.Group): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const material = child.material as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial

      if (material.isMeshStandardMaterial || material.isMeshBasicMaterial) {
        material.transparent = true
        material.opacity = 0.8
      }
    }
  })
}

/**
 * Create a single-row preview building from form values
 * @deprecated Use createPreviewBuilding('single_row', params, worldPosition) instead
 *
 * @param params - SingleRowParams form values
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createSingleRowPreview(
  params: SingleRowParams,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('single_row', params, worldPosition)
  return result || new THREE.Group()
}

/**
 * Create a double-row preview building from form values
 * @deprecated Use createPreviewBuilding('double_row', params, worldPosition) instead
 *
 * @param params - DoubleRowParams form values
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createDoubleRowPreview(
  params: DoubleRowParams,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('double_row', params, worldPosition)
  return result || new THREE.Group()
}

/**
 * Create a T-hangar preview building from form values
 *
 * @param params - THangarParams form values
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createTHangarPreview(
  params: THangarParams,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('standard_t_hangar', params, worldPosition)
  return result || new THREE.Group()
}

/**
 * Create a nested T-hangar preview building from form values
 *
 * @param params - NestedTHangarParams form values
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createNestedTHangarPreview(
  params: NestedTHangarParams,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('nested_t_hangar', params, worldPosition)
  return result || new THREE.Group()
}

/**
 * Create an arch hangar preview building from form values
 *
 * @param params - ArchHangarParams form values
 * @param worldPosition - Position in feet [x, y, z] relative to map center
 * @returns Three.js Group containing the building geometry
 */
export function createArchHangarPreview(
  params: ArchHangarParams,
  worldPosition: [number, number, number]
): THREE.Group {
  const result = createPreviewBuilding('arch_hangar', params, worldPosition)
  return result || new THREE.Group()
}
